<template>
    <div class="c-timeline clearfix" ref="entireTimeline">
        <transition name="fade">
            <a v-if="showPreviousArrow" class="c-timeline__arrow-container" @click="toPrevPage" style="left: 3%;">
                <smart-icon icon="eva-arrow-ios-back-outline" icon-color="#FFFFFF" />
            </a>
        </transition>

        <transition name="fade">
            <a v-if="showNextArrow" class="c-timeline__arrow-container" @click="toNextPage" style="right: 0;">
                <smart-icon icon="eva-arrow-ios-forward-outline" icon-color="#FFFFFF" />
            </a>
        </transition>

        <div
            class="c-timeline__row"
            v-click-outside="{
              handler: onClickOutside,
              include: include
            }"
            :style="{
                width : (columnWidth * (columnCount + amountOfDummyColumns)) + 'px',
                transform: 'translateX(-'+ activeIndexInternal*columnWidth +'px)',
                maxWidth: maxTimelineWidth + 'px'
            }"
        >
                <a
                    v-for="(timelineItem, timelineItemIndex) in timelineItems"
                    :key="timelineItem.key ? timelineItem.key : timelineItemIndex"
                    class="c-timeline__column"
                    :class="[
                      selectedKeyInternal && selectedKeyInternal === timelineItem.key ? 'c-timeline__column--active' : null,
                      timelineItem.key.includes('-01') && timelineItemIndex > activeIndexInternal ? 'c-timeline__column-year' : null
                    ]"
                    :style="{width : columnWidth + 'px'}"
                    @click="(event) => handleClickColumn(event, timelineItem)"
                    :data-year="timelineItem.key.substring(0, 4)"
                >
                    <div class="inline-block">
                        <div
                            v-for="(timelineBlock, index) in reverse(timelineItem.line)"
                            :key="index"
                        >
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <div
                                        v-on="on"
                                        v-bind="attrs"
                                        class="c-timeline__entry"
                                        :style="{width : blockWidth + 'px', height : blockWidth + 'px'}"
                                    >
                                        <div
                                            v-if="timelineBlock.text || timelineBlock.text === 0"
                                            class="c-timeline__entrytext"
                                            :style="{left : (blockWidth - 5) + 'px'}"
                                        >
                                            <div></div>
                                            <span>{{ timelineBlock.text }}</span>
                                        </div>
                                        <smart-icon :icon="timelineBlock.icon" class="relative" :style="{top : iconY + 'px'}"></smart-icon>
                                    </div>
                                    <div class="c-timeline__spacer" :style="{width : blockWidth + 'px'}">
                                        <div
                                            class="c-timeline__spacerline"
                                            :style="{height : spacerLength + 'px', left : -(blockWidth/2) + 'px'}"
                                        ></div>
                                    </div>
                                </template>
                                <span>{{ $ucfirst($t(index === 0 ? 'knowledge.numItemsSent' : 'knowledge.numItemsViewed')) }}</span>
                            </v-tooltip>
                        </div>
                    </div>
                    <div class="c-timeline__spacer" :style="{width : columnWidth + 'px'}">
                        <div class="c-timeline__spacerline" :style="{height : 20 + 'px', left : -(columnWidth/2) + 'px'}" />
                    </div>
                    <div class="c-timeline__label">
                        {{ timelineItem.label }}
                    </div>
                </a>

            <div v-for="n in amountOfDummyColumns" class="c-timeline__column" :key="n">
                <div :style="{height : dummyColumnLength + 'px'}">

                </div>
                <div class="c-timeline__label" :style="{width : columnWidth + 'px'}">
                    <span style="visibility: hidden">*</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SmartIcon from "../SmartIcon";
import moment from "moment";

/**
 * Component Timeline.vue
 *
 * @module Timeline
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.8] app/src/APP_DASHBOARD/templates/components/Parts/Timeline/Timeline.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "Timeline",
  components: {SmartIcon},
  computed: {
    columnCount : function(){
        return this.timelineItems ? this.timelineItems.length : 0;
    },
    showNextArrow : function(){
      return this.activeIndexInternal < this.columnCount - this.pageSize - 1;
    },
    showPreviousArrow : function(){
      return this.activeIndexInternal > 0;
    },
    dummyColumnLength : function(){
      if (this.timelineItems){
        const blockCount = this.timelineItems.length ? this.timelineItems[0].line.length : 0;
        return 20 + blockCount * (this.blockWidth + this.spacerLength);
      }

      return 0;
    }
  },
  data: () => {
    return {
        amountOfDummyColumns: 20,
        activeIndexInternal: 0,
        selectedKeyInternal: null,
        maxTimelineWidth: 1300,
    }
  },
  methods: {
    handleClickColumn : function(event, timelineItem){
      if (this.selectedKeyInternal && this.selectedKeyInternal === timelineItem.key){
        // unselect if it was already selected
        this.selectedKeyInternal = null
      }else{
        this.selectedKeyInternal = timelineItem.key
      }
    },
    toNextPage : function(){
      this.activeIndexInternal = Math.min(this.columnCount, this.activeIndexInternal + this.pageSize)
    },
    toPrevPage : function(){
      this.activeIndexInternal = Math.max(0, this.activeIndexInternal - this.pageSize)
    },
    reverse : function(array){
      return array.slice().reverse()
    },
    onClickOutside() {
      if (this.selectedKeyInternal !== null) {
        this.selectedKeyInternal = null
      }
    },
    include() {
      return [document.querySelector('.included')]
    },
    setMaxTimelineWidth() {
      const timeline = this.$refs.entireTimeline;
      this.maxTimelineWidth = timeline.clientWidth - 40;
    }
  },
  created() {
    this.activeIndexInternal = this.activeIndex;
    this.selectedKeyInternal = this.selectedKey;
    // adjust timeline width based on window size
    window.addEventListener("resize", this.setMaxTimelineWidth);
  },
  destroyed() {
    window.removeEventListener("resize", this.setMaxTimelineWidth);
  },
  mounted() {
    this.setMaxTimelineWidth()
  },
  watch : {
    timelineItems: function(next, prev){
      // reset index to 0 if a new data request is made. first try to see if same key exists in new and old request and set
      // active index to the index of that key instead of resetting to 0,
      // this way the viewer stays at the same month when the data changes if possible.

      if (prev.length){
        const oldKey = prev[this.activeIndexInternal].key
        // if new keys are before the old one - reset to 0
        const beforePrevious = next.some(item => moment(item.key).isBefore(oldKey) && !prev.map(p => p.key).includes(item.key))

        for (let i=0; i<next.length; i++){
          if (next[i].key === oldKey && !beforePrevious){
            this.activeIndexInternal = i
            return
          }
        }
      }

      this.activeIndexInternal = 0
    },
    activeIndexInternal: function(next){
      this.$emit('update:activeIndex', next)
    },
    selectedKeyInternal: function(next){
      this.$emit('update:selectedKey', next)
    },
    activeIndex: function(next){
      this.activeIndexInternal = next
    },
    selectedKey: function(next){
      this.selectedKeyInternal = next
    }
  },
  props: {
    // if a column is currently selected, this will hold the key
    selectedKey : {
      default: () => null
    },

    // the current index of the first shown column
    activeIndex : {
      type: Number,
      default: () => 0
    },

    // amount of columns need to move on next/previous
    pageSize: {
      type: Number,
      default: () => 3
    },

    // column width of columns in px
    columnWidth: {
      type: Number,
      default: () => 200
    },

    // width and height of icon blocks
    blockWidth: {
      type: Number,
      default: () => 52
    },

    // vertical offset in px for icon inside block (used to center icon vertically)
    iconY: {
      type: Number,
      default: () => 12
    },

    // size of the dotted line between icon blocks
    spacerLength: {
      type: Number,
      default: () => 12
    },

    // the data to fill timeline items (see below example data)
    timelineItems: {
      type: Array,
      default: () => []
    },
  },
};

/*
* example timeline data
* [
        {
          key: '2020-01',
          label: 'January',
          line: [
            {icon: "eva-video-outline", text: 3},
            {icon: "eva-eye-outline", text: 2}
          ]
        },
        {
          key: '2020-02',
          label: 'February',
          line: [
            {icon: "eva-video-outline", text: 1},
            {icon: "eva-eye-outline", text: 1}
          ]
        },
        {
          key: '2020-03',
          label: 'March',
          line: [
            {icon: "eva-video-outline", text: 4},
            {icon: "eva-eye-outline", text: 3}
          ]
        },
        {
          key: '2020-04',
          label: 'April',
          line: [
            {icon: "eva-video-outline", text: 3},
            {icon: "eva-eye-outline", text: 1}
          ]
        },
        {
          key: '2020-05',
          label: 'May',
          line: [
            {icon: "eva-video-outline", text: 1},
            {icon: "eva-eye-outline", text: 0}
          ]
        },
        {
          key: '2020-06',
          label: 'June',
          line: [
            {icon: "eva-video-outline", text: 0},
            {icon: "eva-eye-outline", text: 0}
          ]
        },
        {
          key: '2020-07',
          label: 'July',
          line: [
            {icon: "eva-video-outline", text: 2},
            {icon: "eva-eye-outline", text: 2}
          ]
        },
        {
          key: '2020-08',
          label: 'August',
          line: [
            {icon: "eva-video-outline", text: 2},
            {icon: "eva-eye-outline", text: 1}
          ]
        },
        {
          key: '2020-09',
          label: 'September',
          line: [
            {icon: "eva-video-outline", text: 3},
            {icon: "eva-eye-outline", text: 2}
          ]
        },
        {
          key: '2020-10',
          label: 'October',
          line: [
            {icon: "eva-video-outline", text: 3},
            {icon: "eva-eye-outline", text: 3}
          ]
        },
        {
          key: '2020-11',
          label: 'November',
          line: [
            {icon: "eva-video-outline", text: 1},
            {icon: "eva-eye-outline", text: 0}
          ]
        },
        {
          key: '2020-12',
          label: 'December',
          line: [
            {icon: "eva-video-outline", text: 1},
            {icon: "eva-eye-outline", text: 1}
          ]
        },
      ]
*
* */
</script>

<style lang="scss">
    @import "../../../../../scss/colors";

    // mobile first
    .c-timeline{
        position: relative;
        padding-bottom: 20px;
    }

    .c-timeline__row{
        white-space: nowrap;
        transition: transform 0.3s;
        display: flex;
        flex-wrap: nowrap;
    }

    .c-timeline__column{
        padding-top: 20px;
        display: block;
        text-align: center;
        float: left;
        position: relative;
        top: 0;
        transition: all 0.3s;
    }

    .c-timeline__column-year {
      &::after {
        content: attr(data-year);
        bottom: -50px;
        position: absolute;
        font-size: 1.1rem;
        left: 56px;
        font-weight: bold;
        color: rgba(0, 0, 0, 0.87);
      }
    }

    .c-timeline__entry {
        position: relative;
        border: 1px solid map-get($coroColors, lightgray2) !important;
        display: block;
        text-align: center;
        border-radius: 6px;
        text-decoration: none !important;
    }

    .c-timeline__entrytext{
        position: absolute;
        user-select: none;
        top: -0.7em;
        color: map-get($coroColors, blue);
        font-size: 0.9em;

        >div{
            display: inline-block;
            margin-right: 0.2em;
            width: 8px;
            height: 8px;
            border-radius: 4px;
            background: map-get($coroColors, blue);
        }
    }

    .c-timeline__spacer{
        text-align: center;
    }

    .c-timeline__spacerline{
        border-right: 1px dashed map-get($coroColors, lightgray2);
        display: block;
        margin: 0 auto;
        position: relative;
    }

    .c-timeline__label{
        border-top: 1px solid #ccc;
        border-bottom: 1px solid #ccc;
        background: map-get($coroColors, lightgray);
        font-weight: bold;
        line-height: 3em;
    }

    .c-timeline__arrow-container{
        position: absolute;
        z-index: 1;
        height: 100%;
        width: 50px;
        display: flex;
        align-items: flex-end;

        >div{
            background: map-get($coroColors, purple);
            border: 1px solid #eee;
            border-radius: 14px;
            width: 26px;
            height: 26px;
            transition: transform 0.3s;
            position: relative;
            top: -13.5%;
        }

        &:hover{
            >div{
                transform: scale(1.3);
            }
        }
    }

    // mobile screens

    // tablet screens

    // desktop/large screens

    // -------------------------------------------------- BEM modifiers ------------------------------------------------
    .c-timeline__column.c-timeline__column--active{
        background: map-get($coroColors, lightblue);
        top: 15px;
        border-radius: 21px;

        .c-timeline__label{
            border: none;
            background: map-get($coroColors, lightblue);
            border-radius: 21px;
        }

        .c-timeline__entry {
            position: relative;
            border: 1px solid map-get($coroColors, blue) !important;
        }

        .c-timeline__spacerline{
            border-right: 1px dashed map-get($coroColors, blue);
        }
    }
</style>